/**
 * localStorage keying for the onboarding wizard's in-progress state.
 *
 * The key used to be the browser-global `onboardingWizardState`. Onboarding a second school in
 * the same browser then restored the first school's answers at mount — which not only pre-filled
 * the wrong data, it made the wizard write preference buckets (mode/setup/...) before the new
 * tenant had seen a single screen. The backend infers onboarding sub-step analytics from those
 * buckets and dedupes each (tenant, sub-step) row forever, so the first write wins permanently
 * and every later genuine action becomes uncountable. Hence: one key per tenant.
 */

const PREFIX = 'onboardingWizardState'

/** Pre-tenant-keying key. Still cleared so a stale one can never be picked up again. */
const LEGACY_KEY = PREFIX

/**
 * The wizard-state key for a tenant, or `null` when there is no tenant id yet — in which case
 * the caller must neither restore nor persist, rather than fall back to a shared key.
 */
export const onboardingWizardStateKey = (
  urddId: string | number | null | undefined
): string | null => {
  const id = urddId == null ? '' : String(urddId).trim()
  return id ? `${PREFIX}:${id}` : null
}

/** Removes the wizard state for every tenant in this browser, plus the legacy global key. */
export const clearAllOnboardingWizardState = (): void => {
  localStorage.removeItem(LEGACY_KEY)
  const keyed: string[] = []
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i)
    if (key && key.startsWith(`${PREFIX}:`)) keyed.push(key)
  }
  keyed.forEach((key) => localStorage.removeItem(key))
}
